import React, { useState, useEffect } from "react";
import {
  Shield,
  User,
  Chrome,
  CheckCircle,
  AlertCircle,
  X,
  ChevronDown,
  Mail,
} from "lucide-react";

import axios from "axios";

const API_BASE_URL = process.env.REACT_APP__API_BASE_URL;

const OAuthLogin = ({ stepParam, checkAuthenticationStatus, userId }) => {
  const [currentStep, setCurrentStep] = useState(stepParam);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [urlError, setUrlError] = useState(null);

  const [showCustomForm, setShowCustomForm] = useState(false);
  const [workspace, setWorkspace] = useState("");
  const [email, setEmail] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (stepParam === 2) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get("error");
    if (errorParam) {
      setUrlError(decodeURIComponent(errorParam));
    }
  }, []);

  const handleAuthorize = async (provider) => {
    setIsAnimating(true);
    setLoading(true);
    setError(null);

    try {
      console.log("Authorization Call");
      const response = await fetch(
        `${API_BASE_URL}/api/oauth/redirect/${provider}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      console.log("Authorization Call 1", data);

      if (data.success && data.redirect_url) {
        window.open(
          data.redirect_url,
          "_blank",
          "width=500,height=600,scrollbars=yes,resizable=yes"
        );
        checkAuthenticationStatus();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Authorization error:", error);
      setError(`Failed to authorize with ${provider}. Please try again.`);
    } finally {
      setLoading(false);
      setIsAnimating(false);
    }
  };

  const handleDefaultSendingMail = async () => {
    setLoading(true);
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");

    const formData = new FormData();
    formData.append("workspace_preference", "default");
    formData.append("workspace_email", "hello@tide360.ai");
    formData.append("user_id", userId);

    const response = await axios.post(
      `${API_BASE_URL}/api/oauth/update_email_prompt`,
      formData,
      {
        headers: { "X-CSRF-TOKEN": csrfToken },
      }
    );

    if (response.data.success) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      setError("There is a problem saving your data");
    }
  };

  const handleCustomEmailSubmit = async () => {
    if (!workspace || !email) {
      setError("Please fill in all fields");
      return;
    }
    setError(null);
    console.log("Custom email form submitted:", { workspace, email });
    setFormSubmitted(true);

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");

    const formData = new FormData();
    formData.append("workspace_preference", workspace);
    formData.append("workspace_email", email);
    formData.append("user_id", userId);
    formData.append("_token", csrfToken);

    const response = await axios.post(
      `${API_BASE_URL}/api/oauth/update_email_prompt`,
      formData,
      {
        headers: { "X-CSRF-TOKEN": csrfToken },
      }
    );

    if (response.data.success) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      setError("There is a problem saving your data");
    }
  };

  const closeUrlError = () => {
    setUrlError(null);
    const url = new URL(window.location);
    url.searchParams.delete("error");
    window.history.replaceState({}, "", url);
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      {urlError && (
        <div className="fixed top-4 left-4 right-4 z-50 max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{urlError}</p>
              </div>
              <div className="flex-shrink-0">
                <button
                  onClick={closeUrlError}
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-400 hover:bg-red-100 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50 transition-colors duration-200"
                >
                  <span className="sr-only">Dismiss</span>
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`bg-white rounded-2xl shadow-2xl p-8 text-center max-w-md w-full transform transition-all duration-300 ${
          isAnimating ? "scale-95 opacity-75" : "scale-100 opacity-100"
        }`}
      >
        <div className="flex justify-center items-center mb-8">
          <div className="flex items-center space-x-4">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                currentStep >= 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-400"
              }`}
            >
              {currentStep > 1 ? (
                <CheckCircle size={20} />
              ) : (
                <span className="text-sm font-bold">1</span>
              )}
            </div>
            <div
              className={`w-12 h-1 transition-all duration-300 ${
                currentStep >= 2 ? "bg-blue-600" : "bg-gray-200"
              }`}
            ></div>
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                currentStep >= 2
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-400"
              }`}
            >
              <span className="text-sm font-bold">2</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {currentStep === 1 && (
          <div className="animate-fade-in">
            <div className="mb-6">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-blue-600" size={40} />
              </div>
              <p className="text-gray-600">Authorize with your Account</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => handleAuthorize("go-high")}
                disabled={loading}
                className="w-full bg-gradient-to-r cursor-pointer from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
              >
                <User size={20} />
                <span>{loading ? "Authorizing..." : "Authorize Now"}</span>
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && !formSubmitted && (
          <div className="animate-fade-in">
            <div className="mb-6">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Chrome className="text-green-600" size={40} />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                Connect External Account
              </h2>
              <p className="text-gray-600">
                Choose how you want to send your emails
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleDefaultSendingMail}
                disabled={loading}
                className="w-full bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-800 font-semibold py-4 px-6 rounded-xl shadow-md hover:shadow-lg transform transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center space-x-3 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
              >
                <span>
                  {loading ? "Loading..." : "Use default (hello@tide360.ai)"}
                </span>
              </button>

              <button
                onClick={() => setShowCustomForm(!showCustomForm)}
                disabled={loading}
                className="w-full bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-800 font-semibold py-4 px-6 rounded-xl shadow-md hover:shadow-lg transform transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center space-x-3 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
              >
                <span>{loading ? "Loading..." : "I want to use my own"}</span>
              </button>

              {/* Custom Email Form */}
              {showCustomForm && (
                <div className="mt-6 p-6 bg-gray-50 rounded-xl border-2 border-gray-200 animate-fade-in">
                  <div className="space-y-4">
                    <div className="text-left">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Which workspace do you use?
                      </label>
                      <div className="relative">
                        <select
                          className="w-full bg-white border-2 border-gray-200 rounded-lg py-3 px-4 text-left appearance-none focus:outline-none focus:border-blue-500 transition-colors duration-200"
                          value={workspace}
                          onChange={(e) => setWorkspace(e.target.value)}
                        >
                          <option value="" disabled>
                            Choose your workspace
                          </option>
                          <option value="google">Google Workspace</option>
                          <option value="microsoft">Microsoft 365</option>
                          <option value="zoho">Zoho Mail</option>
                          <option value="hubspot">HubSpot</option>
                          <option value="outlook">Outlook</option>
                          <option value="yahoo">Yahoo Mail</option>
                          <option value="other">Other</option>
                        </select>
                        <ChevronDown
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                          size={20}
                        />
                      </div>
                    </div>

                    <div className="text-left">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        What is your email?
                      </label>
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        className="w-full bg-white border-2 border-gray-200 rounded-lg py-3 px-4 focus:outline-none focus:border-blue-500 transition-colors duration-200"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <button
                      onClick={handleCustomEmailSubmit}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-500 text-center mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3">
                You will be able to set it up later as well.
              </p>
            </div>
          </div>
        )}

        {currentStep === 2 && formSubmitted && (
          <div className="animate-fade-in">
            <div className="mb-6">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Thank You!
              </h2>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed">
                You will be using default email sending for now and we will
                reach out to you with your workspace integration built in{" "}
                <strong>3 working days</strong>. Thank you for being a{" "}
                <strong>Tide360</strong> customer.
              </p>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OAuthLogin;
